'use client';

import { useRef, useEffect, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';

interface BackgroundModelProps {
  disableScroll?: boolean;
}

export default function BackgroundModel({ disableScroll = false }: BackgroundModelProps) {
  const groupRef = useRef<THREE.Group>(null);
  const scrollProgress = useRef(0);
  const lerpProgress = useRef(0);

  // Load the GLB model uploaded by the user
  const { scene } = useGLTF('/models/wadapi_3d_02.glb');

  // Clone the scene to ensure isolated instantiation
  const clonedScene = useMemo(() => scene.clone(), [scene]);

  // Adjust materials in the model to integrate beautifully with studio lighting
  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        
        if (child.material) {
          const mat = child.material as THREE.MeshStandardMaterial;
          mat.roughness = 0.55; 
          mat.metalness = 0.1;  
          
          if (child.material instanceof THREE.MeshPhysicalMaterial) {
            const physMat = child.material as THREE.MeshPhysicalMaterial;
            physMat.sheen = 1.2;
            physMat.sheenColor = new THREE.Color('#FFA3C4');
            physMat.sheenRoughness = 0.5;
          }
        }
      }
    });
  }, [clonedScene]);

  // Listen to native page scrolling
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        scrollProgress.current = scrollTop / docHeight;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update dynamic animation properties per frame
  useFrame((state) => {
    const elapsed = state.clock.getElapsedTime();
    lerpProgress.current += (scrollProgress.current - lerpProgress.current) * 0.08;

    if (groupRef.current) {
      // Configuration fixed based on user input
      const posX = 8.4;
      const posY = 6.4;
      const posZ = 0.8;
      const rotX = -0.14;
      const rotY = -1.99;
      const rotZ = 0.16;
      const scale = 4.1;

      if (disableScroll) {
        // Model stays static with breathing effect
        groupRef.current.position.set(posX, posY, posZ);
        groupRef.current.rotation.set(
          rotX,
          rotY + Math.sin(elapsed * 0.1) * 0.04,
          rotZ
        );
      } else {
        // Scroll animations
        const baseRotY = lerpProgress.current * Math.PI * 0.28 + Math.sin(elapsed * 0.1) * 0.02;
        const baseRotX = lerpProgress.current * Math.PI * 0.06;
        const basePosZ = lerpProgress.current * 4.0;
        const basePosX = -lerpProgress.current * 1.5;

        groupRef.current.position.set(
          basePosX + posX,
          posY,
          basePosZ + posZ
        );
        groupRef.current.rotation.set(
          baseRotX + rotX,
          baseRotY + rotY,
          rotZ
        );
      }
      groupRef.current.scale.setScalar(scale);
    }
  });

  return (
    <group ref={groupRef} dispose={null}>
      <primitive object={clonedScene} />
    </group>
  );
}

// Preload to optimize page speed
useGLTF.preload('/models/wadapi_3d_02.glb');
